import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Card from '../components/common/Card';
import CardContent from '../components/common/CardContent';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const { login, loading, loginSuccess, activeSessionId } = useAuth();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const loginData = {
      email: formData.email,
      password: formData.password,
      action: 'signin',
      sessionId: activeSessionId
    };

    setIsLoggingIn(true);

    // Send data to n8n webhook
    try {
      const response = await fetch('https://nds1234567.app.n8n.cloud/webhook/933ce8d9-e632-45dc-9144-87188d27666a', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      const data = await response.json();

      // Handle n8n response which might be an array or object
      const responseItem = Array.isArray(data) ? data[0] : data;
      // Handle wrapped json or direct object
      const itemData = responseItem.json || responseItem;

      const status = itemData.status;
      const message = itemData.message;
      const success = itemData.success; // Check for boolean success
      const statusCode = itemData.statusCode; // Check for 200 OK

      console.log('Webhook Data:', data);
      console.log('Resolved Status:', status, 'Success:', success, 'StatusCode:', statusCode);

      // Check for success (support older string format and new boolean/code format)
      const isSuccess =
        (status && status.toString().trim().toLowerCase() === 'success') ||
        (success === true) ||
        (statusCode === 200);

      if (isSuccess) {
        console.log('Login successful. Starting session and navigating...');
        // Start session
        try {
          loginSuccess({ email: formData.email, name: 'User' }); // Minimal user object

          // Pre-fetch search results for Discover page
          try {
            const searchResponse = await fetch('https://nds1234567.app.n8n.cloud/webhook/ac5d8037-976d-4384-8622-a08566629e3e', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                intent: 'Search',
                query: 'Maldives, Hanoi, Kuala Lumpur, Tokyo, Bali, Ibiza, Rome',
                email: formData.email,
                sessionId: activeSessionId
              })
            });
            const searchData = await searchResponse.json();
            navigate('/discover', { state: { n8nData: searchData } });
          } catch (searchError) {
            console.error('Pre-fetch search failed:', searchError);
            navigate('/discover');
          }

        } catch (err) {
          alert(`Error processing login: ${err.message}`);
          console.error(err);
        }
        return;
      } else {
        console.log('Login failed with status:', status || success || statusCode);
        // alert(`Login Failed. Status: ${status}, Message: ${message}`);
        setErrors({ general: message || 'Invalid email or password.' });
      }
    } catch (error) {
      console.error('Failed to send data to webhook:', error);
      alert(`Connection Error: ${error.message}`);
      setErrors({ general: 'Connection error. Please try again later.' });
    } finally {
      setIsLoggingIn(false);
    }

    // Backend login is updated to use webhook
    // const result = await login(formData.email, formData.password);
    // if (result.success) {
    //   navigate(from, { replace: true });
    // } else {
    //   setErrors({ general: result.error });
    // }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 relative">
      {isLoggingIn && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-[9999] flex flex-col items-center justify-center rounded-2xl">
          <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mb-4"></div>
          <h3 className="text-xl font-semibold text-gray-800">Signing you in...</h3>
          <p className="text-gray-500 mt-2">Just a moment.</p>
        </div>
      )}
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-display font-bold text-gray-900">
            Welcome Back
          </h2>
          <p className="mt-2 text-gray-600">
            Sign in to your account to continue your journey
          </p>
        </div>

        <Card>
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.general && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 text-sm">{errors.general}</p>
                </div>
              )}

              <Input
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={errors.email}
                placeholder="Enter your email"
                icon={<Mail className="w-5 h-5 text-gray-400" />}
              />

              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={errors.password}
                  placeholder="Enter your password"
                  icon={<Lock className="w-5 h-5 text-gray-400" />}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Remember me</span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  Forgot password?
                </Link>
              </div>

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoggingIn}
              >
                Sign In
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="text-primary-600 hover:text-primary-500 font-medium"
                >
                  Sign up
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
